import { getLocale, translate } from 'shared/lib/i18n.tsx';

import { parseChefMasterFrame, type ChefMasterHand } from './chefMasterFrame.ts';

/**
 * Chef Master на ТВ (GEN-18423). Правила и отрисовка — та же страница, что и у
 * телефона (`src/games/chef-master/page`, копия в `dist/client/games/chef-master`),
 * только в iframe 16:9. Телефон — камера: считает руки и шлёт кадры
 * `{type: 'ChefMasterData', gameState, hands}` ~30 раз/с; ТВ передаёт их в
 * `grooveSetHands`. Счёт страница отдаёт сообщением `finished` → `onFinished` →
 * `game:state gameover`.
 *
 * Пауза, возобновление и рестарт идут полем `gameState` кадров (канал без
 * гарантий доставки — телефон повторяет их каждые 250 мс; повторы здесь
 * безвредны, действие выполняется только при смене фазы).
 */
export type ChefMasterPhase = 'lobby' | 'calibration' | 'playing' | 'paused' | 'gameover';

/** Методы страницы (контракт веб-игр v1). */
interface ChefMasterPageApi {
  grooveStart(): void;
  groovePause(): void;
  grooveResume(): void;
  grooveRestart(): void;
  grooveSetHands(payload: { hands: ChefMasterHand[] }): void;
}

const Z_INDEX = '1';
const PAGE_URL = '/pages/games/chef-master/index.html';
/** Фон iframe: страница прозрачна (на телефоне под ней камера), на ТВ — кухня. */
const KITCHEN_COLOR = '#f6e7cf';
/** Страница должна отозваться `ready` за это время, иначе показываем ошибку. */
const LOAD_TIMEOUT_MS = 15_000;

export class ChefMasterDisplayEngine {
  private phase: ChefMasterPhase = 'lobby';

  private host: HTMLDivElement | null = null;

  private frame: HTMLIFrameElement | null = null;

  private statusEl: HTMLDivElement | null = null;

  private pageReady = false;

  /** `start-game` пришёл, `grooveStart()` ещё не вызван (страница не успела загрузиться). */
  private startPending = false;

  private pageStarted = false;

  /** После последнего (ре)старта приходили кадры `STARTED`. */
  private sawStarted = false;

  private loadTimer = 0;

  onFinished?: (score: number) => void;

  getPhase(): ChefMasterPhase {
    return this.phase;
  }

  /** `select-game`: поднимаем страницу и ждём `start-game`. Повторный вызов — рестарт. */
  startCalibration(): void {
    if (this.host) {
      this.api()?.grooveRestart();
      this.startPending = false;
      this.sawStarted = false;
    } else {
      this.createHost();
    }
    this.phase = 'calibration';
    this.showStatus(null);
  }

  /** `start-game`: страница показывает «покажи обе ладони» и ждёт руки. */
  startGame(): void {
    if (!this.host) this.createHost();
    this.phase = 'playing';
    this.showStatus(null);
    this.startPage();
  }

  onGameFrame(rawFrame: unknown): void {
    const frame = parseChefMasterFrame(rawFrame);
    const page = this.api();
    if (!frame || !page || this.phase === 'lobby' || this.phase === 'calibration') return;

    if (frame.gameState === 'PREPARING') {
      // Рестарт с телефона: чистая кухня и снова «покажи обе ладони». Повторы кадра
      // (и кадры до начала забега) не в счёт — рестартуем, только если после прошлого
      // рестарта приходили кадры игры.
      if (this.phase === 'playing' && !this.sawStarted) return;
      page.grooveRestart();
      this.sawStarted = false;
      this.phase = 'playing';
      this.showStatus(null);
      return;
    }
    if (this.phase === 'gameover') return;

    if (frame.gameState === 'PAUSED') {
      if (this.phase !== 'playing') return;
      page.groovePause();
      this.phase = 'paused';
      this.showStatus(translate('game.pause'));
      return;
    }
    if (frame.gameState !== 'STARTED') return;
    this.sawStarted = true;
    if (this.phase === 'paused') {
      page.grooveResume();
      this.phase = 'playing';
      this.showStatus(null);
    }
    if (frame.hands) page.grooveSetHands({ hands: frame.hands });
  }

  reset(): void {
    this.phase = 'lobby';
    this.pageReady = false;
    this.startPending = false;
    this.pageStarted = false;
    this.sawStarted = false;
    window.clearTimeout(this.loadTimer);
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('resize', this.fit);
    this.host?.remove();
    this.host = null;
    this.frame = null;
    this.statusEl = null;
  }

  private createHost(): void {
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};background:#1b1410;`;

    const frame = document.createElement('iframe');
    const lang = getLocale() === 'kk' ? 'kz' : getLocale();
    frame.src = `${PAGE_URL}?platform=tv&lang=${lang}`;
    frame.setAttribute('scrolling', 'no');
    frame.style.cssText = `position:absolute;border:0;background:${KITCHEN_COLOR};`;
    host.appendChild(frame);

    const status = document.createElement('div');
    status.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:2;'
      + 'padding:24px 40px;border-radius:24px;background:rgba(20,14,50,.75);color:#fff;'
      + 'font:800 40px -apple-system,system-ui,sans-serif;text-align:center;display:none;';
    host.appendChild(status);

    document.body.appendChild(host);
    this.host = host;
    this.frame = frame;
    this.statusEl = status;
    this.pageReady = false;
    this.pageStarted = false;
    window.addEventListener('message', this.onMessage);
    window.addEventListener('resize', this.fit);
    this.fit();
    window.clearTimeout(this.loadTimer);
    this.loadTimer = window.setTimeout(() => {
      if (!this.pageReady) this.showStatus(translate('game.chef_load_failed'));
    }, LOAD_TIMEOUT_MS);
  }

  /** Самый большой прямоугольник 16:9, который помещается в экран, по центру. */
  private readonly fit = (): void => {
    const { frame } = this;
    if (!frame) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.floor(Math.min(vw, (vh * 16) / 9));
    const h = Math.floor((w * 9) / 16);
    frame.style.width = `${w}px`;
    frame.style.height = `${h}px`;
    frame.style.left = `${Math.floor((vw - w) / 2)}px`;
    frame.style.top = `${Math.floor((vh - h) / 2)}px`;
  };

  private readonly onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin || event.source !== this.frame?.contentWindow) return;
    const { data } = event as { data: unknown };
    const message = data && typeof data === 'object' ? (data as Record<string, unknown>).groove : undefined;
    if (!message || typeof message !== 'object') return;
    const { type, score, message: text } = message as Record<string, unknown>;

    if (type === 'ready') {
      this.pageReady = true;
      window.clearTimeout(this.loadTimer);
      // Страница ответила после таймаута — плашка «не удалось загрузить» уже на экране.
      this.showStatus(null);
      if (this.startPending) this.startPage();
    } else if (type === 'finished') {
      const value = typeof score === 'number' && Number.isFinite(score) ? score : 0;
      this.phase = 'gameover';
      // Страница очищает канву в конце игры — итог рисуем сами.
      this.showStatus(translate('game.chef_time_up', { score: value }));
      this.onFinished?.(value);
    } else if (type === 'error') {
      // eslint-disable-next-line no-console
      console.warn('[ChefMaster] page error', text);
    }
  };

  private startPage(): void {
    if (this.pageStarted) return;
    const page = this.api();
    if (!page) {
      this.startPending = true;
      return;
    }
    this.startPending = false;
    this.pageStarted = true;
    page.grooveStart();
  }

  /** Окно страницы, когда она загрузилась и сказала `ready`; иначе `null`. */
  private api(): ChefMasterPageApi | null {
    if (!this.pageReady) return null;
    return (this.frame?.contentWindow as unknown as ChefMasterPageApi | null | undefined) ?? null;
  }

  private showStatus(text: string | null): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text ?? '';
    this.statusEl.style.display = text ? 'block' : 'none';
  }
}
